import React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled, { css } from 'styled-components';
import classNames from 'classnames';

// constants
import { colors, spacing } from '../../style';
import Group, { GroupProps } from '../group';
import type { Weights } from '../title';

export interface TabProps {
  className?: string;
  href?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  active?: boolean;
  size?: keyof typeof sizes;
  componentClass?: React.ElementType | string;
  to?: string | RouteComponentProps['location'];
  exact?: boolean;
  onClick?: React.MouseEventHandler<any>;
  flexShrink?: number;
  weight?: Weights;
}

export const sizes = {
  lg: css`
    padding: 8px 0;
    font-size: 1rem;
  `,
  xl: css`
    font-size: 1rem;
    height: 50px;
    padding: 6px 16px;
  `,

  xs: css`
    padding: 8px 0;
    font-size: 0.75rem;
  `,
};

const TabComponent = ({
  className,
  disabled,
  active,
  componentClass: Component = 'button',
  to,
  weight: _weight,
  ...rest
}: TabProps) => (
  <Component
    type={Component === 'button' ? 'button' : undefined}
    {...(to ? { activeClassName: 'active' } : {})}
    to={to}
    className={classNames(className, { disabled, active })}
    disabled={disabled}
    role="tab"
    aria-selected={!!active}
    {...rest}
  />
);

const Tab = styled(TabComponent)`
  border: none;
  background: transparent;
  border-bottom: 2px solid transparent;
  color: ${colors.gray900};
  font-weight: ${props => props.weight ?? 500};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 120px;

  flex-shrink: ${props => props.flexShrink};

  ${props => sizes[props.size || 'lg']};

  &:not([disabled]),
  &:not(.disabled) {
    cursor: pointer;
  }

  &:hover {
    color: ${props => props.theme.actionColor || colors.action};
  }

  &:active {
    ${props => `color: ${props.theme.actionColorDark}`};
  }

  &.active {
    color: ${props => props.theme.actionColor || colors.action};
    border-bottom: 2px solid ${props => props.theme.actionColor};
  }

  &.disabled {
    color: ${colors.gray600};
    border-color: ${colors.gray600};
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px ${colors.sky};
  }
`;

interface TabGroupProps extends GroupProps {
  'aria-label': string;
  variant?: keyof typeof variants;
  offsetBottom?: keyof typeof offsets;
  className?: string;
}

export const variants = {
  'border-bottom-gray300': css`
    border-bottom: 2px solid ${colors.gray300};
  `,
};

export const offsets = {
  tiny: spacing.tiny,
};

export const TabGroup = styled((props: TabGroupProps) => {
  return <Group role="tablist" sideItemsMargin={'sm'} bottomItemMargin={'none'} {...props} />;
}).withConfig({
  shouldForwardProp: prop => !['variant', 'offsetBottom'].includes(prop),
})`
  ${props => props.variant && variants[props.variant]}
  ${props =>
    props.offsetBottom &&
    `
	& > [role="tab"] {
		margin-bottom: -${offsets[props.offsetBottom]} !important;
	}
	`}
`;

export default Tab;
